import React, { useState } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Chip,
  Box,
  CardActions,
  Divider,
  Tooltip,
  useTheme,
  CardHeader,
  Avatar,
  Paper,
  alpha,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ForkRightIcon from "@mui/icons-material/ForkRight";
import HistoryIcon from "@mui/icons-material/History";
import SnippetModal from "./SnippetModal";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";

const SnippetCard = ({
  subHeaderTitle,
  snippet,
  onDelete,
  onEdit,
  showActions = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [originalSnippet, setOriginalSnippet] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [originalSnippetModalOpen, setOriginalSnippetModalOpen] =
    useState(false);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  const theme = useTheme();

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    onDelete(snippet._id);
  };

  const handleEditClick = () => {
    handleMenuClose();
    onEdit(snippet._id);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchOriginalSnippet = async () => {
    if (!snippet.forkedFrom) return;

    handleMenuClose();
    setIsLoadingOriginal(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/original-snippet/${snippet.forkedFrom}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (data.message === "Invalid token") {
        // Remove user from local storage
        dispatch(logout());
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fork snippet");
      }

      setOriginalSnippet(data.snippet);
      setOriginalSnippetModalOpen(true);
    } catch (error) {
      console.error("Error fetching original snippet:", error);
      setSnackbarMessage("Failed to fetch original snippet. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsLoadingOriginal(false);
    }
  };

  const forkCode = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/fork-snippet`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ originalSnippetId: snippet._id }),
        }
      );

      const data = await response.json();

      if (data.message === "Invalid token") {
        // Remove user from local storage
        dispatch(logout());
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fork snippet");
      }

      setSnackbarMessage(`Snippet forked successfully: ${data.snippet.title}`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error forking snippet:", error);
      setSnackbarMessage("Failed to fork snippet. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Get language-specific icon color
  const getLanguageColor = (language) => {
    const colors = {
      javascript: "#F7DF1E",
      python: "#306998",
      java: "#ED8B00",
      cpp: "#00599C",
      ruby: "#CC342D",
      typescript: "#007ACC",
      html: "#E44D26",
      css: "#2965F1",
      rust: "#DEA584",
      go: "#00ADD8",
    };

    return colors[language.toLowerCase()] || theme.palette.primary.main;
  };

  // Show only first 5 lines in the card when not expanded
  const truncatedCode =
    snippet.code
      .split("\n")
      .slice(0, expanded ? undefined : 5)
      .join("\n") +
    (!expanded && snippet.code.split("\n").length > 5 ? "\n..." : "");

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(snippet.code);
    setSnackbarMessage("Snippet copied to clipboard!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  return (
    <>
      <Card
        elevation={3}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: 6,
          },
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              sx={{
                bgcolor: alpha(getLanguageColor(snippet.language), 0.8),
                color: theme.palette.getContrastText(
                  getLanguageColor(snippet.language)
                ),
              }}
            >
              {snippet.language.charAt(0).toUpperCase()}
            </Avatar>
          }
          title={snippet.title}
          subheader={`${subHeaderTitle} : ${
            subHeaderTitle === "Created By"
              ? snippet.username
              : new Date(snippet.createdAt).toLocaleDateString("en-GB")
          }  | ${snippet.language}`}
          titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
          subheaderTypographyProps={{ variant: "body2" }}
          sx={{ pb: 1 }}
          action={
            showActions && (
              <IconButton aria-label="settings" onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            )
          }
        />

        <Divider />

        <CardContent sx={{ pt: 2, pb: 1, flexGrow: 1 }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Tooltip title="Time Complexity">
              <Chip
                icon={<AccessTimeIcon />}
                label={snippet.timeComplexity}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 1 }}
              />
            </Tooltip>
            <Tooltip title="Space Complexity">
              <Chip
                icon={<DataUsageIcon />}
                label={snippet.spaceComplexity}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 1 }}
              />
            </Tooltip>

            {snippet.forkedFrom && (
              <Tooltip title="Forked snippet (click to view original)">
                <Chip
                  icon={<HistoryIcon />}
                  label="Forked"
                  size="small"
                  color="secondary"
                  onClick={fetchOriginalSnippet}
                  sx={{
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>

          <Paper
            variant="outlined"
            sx={{
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha("#000", 0.7)
                  : alpha("#f5f5f5", 0.8),
              p: 1.5,
              position: "relative",
              borderRadius: 1,
              fontFamily: "monospace",
              maxHeight: expanded ? "none" : "150px",
              overflow: "hidden",
              cursor: "pointer",
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha("#000", 0.8)
                    : alpha("#f5f5f5", 0.9),
              },
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                gap: 0.5,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                borderRadius: 1,
                p: 0.5,
              }}
            >
              <Tooltip title="Copy Code">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fork Code">
                <IconButton size="small" onClick={forkCode}>
                  <ForkRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="View Full Screen">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalOpen();
                  }}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              component="pre"
              sx={{
                m: 0,
                fontSize: "0.875rem",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: theme.palette.text.primary,
              }}
            >
              {truncatedCode}
            </Box>
          </Paper>
        </CardContent>

        {snippet.tags && snippet.tags.length > 0 && (
          <>
            <Divider />
            <CardActions sx={{ p: 1.5, pt: 1, flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <FolderSpecialIcon fontSize="small" color="action" />
                {snippet.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    sx={{
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  />
                ))}
              </Box>
            </CardActions>
          </>
        )}
      </Card>

      {/* Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {snippet.forkedFrom && (
          <MenuItem onClick={fetchOriginalSnippet} disabled={isLoadingOriginal}>
            <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
            {isLoadingOriginal
              ? "Loading original..."
              : "View Original Snippet"}
          </MenuItem>
        )}
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Snippet
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Snippet
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Snippet</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{snippet.title}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Current Snippet Modal */}
      <SnippetModal
        open={modalOpen}
        handleClose={handleModalClose}
        originalCode={snippet.code}
        title={snippet.title}
        language={snippet.language}
        snippetId={snippet._id}
        isForked={!!snippet.forkedFrom} // Determines if the modal is read-only
      />

      {/* Original Snippet Modal */}
      {originalSnippet && (
        <SnippetModal
          open={originalSnippetModalOpen}
          handleClose={() => setOriginalSnippetModalOpen(false)}
          originalCode={originalSnippet.code}
          title={`Original: ${originalSnippet.title}`}
          language={originalSnippet.language}
          snippetId={originalSnippet._id}
          additionalInfo={`Created by: ${originalSnippet.username}`}
          isForked={false} // Original snippets are not forked
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SnippetCard;
